import { api } from "./client";
import { apiRoutes, buildApiAbsoluteUrl } from "util/apiRoutes";
import type {
  QuizDetail,
  QuizInviteResult,
  QuizIssueRanking,
  QuizPostLite,
  QuizResults,
  QuizSubmitTeabatePayload,
} from "types/quiz";

interface WrappedResponse<T> {
  data: T;
}

export interface QuizGuessInput {
  targetUserId: number;
  guessedIssueId: number;
}

export interface QuizResponseInput {
  quizTeabateIds: number[];
  response: number;
  importance: number;
}

/**
 * Quiz domain API wrapper.
 */
export const quizApi = {
  async createQuiz(): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.root);
    return response.data.data;
  },

  async getQuiz(code: string): Promise<QuizDetail> {
    const response = await api.get<WrappedResponse<QuizDetail>>(apiRoutes.quiz.byCode(code));
    return response.data.data;
  },

  async getIssueTeabates(issueId: number): Promise<QuizPostLite[]> {
    const response = await api.get<QuizPostLite[]>(apiRoutes.posts.root, {
      params: {
        issueId,
        kind: "teabate",
        limit: 20,
        offset: 0,
        sortField: "created_at",
        sortOrder: "DESC",
      },
    });
    return response.data.filter((post) => {
      const dimension = String(post.properties?.dimension || "").trim().toLowerCase();
      return Boolean(dimension && dimension !== "none");
    });
  },

  async joinQuiz(code: string): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.join(code));
    return response.data.data;
  },

  getQuizQrUrl(code: string): string {
    return buildApiAbsoluteUrl(apiRoutes.quiz.qr(code));
  },

  async sendInvites(code: string, emails: string[]): Promise<QuizInviteResult> {
    const response = await api.post<WrappedResponse<QuizInviteResult>>(apiRoutes.quiz.invites(code), {
      emails,
    });
    return response.data.data;
  },

  async startQuiz(code: string): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.start(code));
    return response.data.data;
  },

  async submitIssueRankings(code: string, rankings: QuizIssueRanking[]): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.issues(code), {
      rankings: rankings.map((ranking) => ({
        issueId: ranking.issue_id,
        rank: ranking.rank,
        sentiment: ranking.sentiment,
      })),
    });
    return response.data.data;
  },

  async submitTeabate(code: string, payload: QuizSubmitTeabatePayload): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.teabate(code), payload);
    return response.data.data;
  },

  async startPart2(code: string): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.startPart2(code));
    return response.data.data;
  },

  async submitGuesses(code: string, guesses: QuizGuessInput[]): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.guesses(code), {
      guesses,
    });
    return response.data.data;
  },

  async submitResponses(code: string, responses: QuizResponseInput[]): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.responses(code), {
      responses,
    });
    return response.data.data;
  },

  async endQuiz(code: string): Promise<QuizDetail> {
    const response = await api.post<WrappedResponse<QuizDetail>>(apiRoutes.quiz.end(code));
    return response.data.data;
  },

  async getResults(code: string): Promise<QuizResults> {
    const response = await api.get<WrappedResponse<QuizResults>>(apiRoutes.quiz.results(code));
    return response.data.data;
  },

  async publish(code: string): Promise<QuizResults> {
    const response = await api.post<WrappedResponse<QuizResults>>(apiRoutes.quiz.publish(code));
    return response.data.data;
  },
};
